// @ts-check
import { DateTime } from "luxon"
import React from "react"
import {
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
} from "../../../../../consts/modules/clientReport.consts"
import { numberFormatter } from "../../../../../utils/numbers.utils"
import Tooltip from "../../../../../components/atoms/Tooltip.atom"

/** @param {import("../../../../../services/clientReport.service.types").ClientSprintLogEntry[]} sprintsData */
function filterOutTimeoutSprints(sprintsData) {
  return sprintsData.filter(
    (sprint) => sprint.closeType !== CLOSE_TYPES.TIMEOUT
  )
}

/**
 * @param {import("../../../../../services/clientReport.service.types").ClientSprintLogEntry[]} sprintsData
 * @param {keyof import("../../../../../services/clientReport.service.types").ClientSprintLogEntry} columnId
 */
function sumColumnValuesWithoutTimeout(sprintsData, columnId) {
  return filterOutTimeoutSprints(sprintsData).reduce((sum, sprint) => {
    const value = Number(sprint[columnId])
    return sum + (isNaN(value) ? 0 : value)
  }, 0)
}

export function buildClientSprintsTableCells() {
  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableCellContext<number>} ctx */
  const SprintCellTemplate = (ctx) => {
    const { row } = ctx
    const sprintNumber = ctx.getValue()
    const rowData = row.original

    return (
      <>
        <span className={`sprint-${rowData.id}`}>
          {rowData.closeType === CLOSE_TYPES.TIMEOUT ?
            CLOSE_TYPE_LABELS.TIMEOUT
          : sprintNumber}
        </span>
        {rowData.closeType === CLOSE_TYPES.TIMEOUT && (
          <Tooltip
            content={
              (rowData.closeReasonRequiresAdditionalText ?
                rowData.closeReasonAdditionalText
              : rowData.closeReasonDescription) ?? undefined
            }
            target={`.sprint-${rowData.id}`}
          />
        )}
      </>
    )
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableCellContext} ctx */
  const PeriodCellTemplate = (ctx) => {
    const { row } = ctx
    const rowData = row.original

    const startDate =
      rowData.startDate ? DateTime.fromISO(rowData.startDate) : null
    const endDate = rowData.endDate ? DateTime.fromISO(rowData.endDate) : null

    if (!startDate) return "-"

    return (
      <div>
        {startDate.toFormat("dd/MM/yyyy")}-
        {endDate ? endDate.toFormat("dd/MM/yyyy") : "En curso"}
      </div>
    )
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableCellContext} ctx */
  const TotalCompletedPointsCellTemplate = (ctx) => {
    const totalCompletedPoints = ctx.getValue()

    if (totalCompletedPoints === null || totalCompletedPoints === undefined) {
      return "-"
    }

    return numberFormatter(totalCompletedPoints)
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableHeaderContext} ctx */
  const TotalCompletedPointsFooterTemplate = (ctx) => {
    const { table } = ctx
    const sprintsData = table.getRowModel().rows.map((row) => row.original)

    const totalCompletedPoints = sumColumnValuesWithoutTimeout(
      sprintsData,
      "totalCompletedPoints"
    )

    return `${numberFormatter(totalCompletedPoints)} pts`
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableHeaderContext<number>} ctx */
  const TotalDevHoursFooterTemplate = (ctx) => {
    const { table } = ctx
    const sprintsData = table.getRowModel().rows.map((row) => row.original)

    const totalDevHours = sumColumnValuesWithoutTimeout(
      sprintsData,
      "totalDevHours"
    )

    return `${numberFormatter(totalDevHours)} hrs`
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableCellContext} ctx */
  const AverageHoursPerPointCellTemplate = (ctx) => {
    const { row } = ctx
    const rowData = row.original

    const totalCompletedPoints = Number(rowData.totalCompletedPoints)
    const totalDevHours = Number(rowData.totalDevHours)

    if (
      isNaN(totalCompletedPoints) ||
      isNaN(totalDevHours) ||
      totalCompletedPoints === 0 ||
      totalDevHours === 0
    )
      return "-"

    return numberFormatter(totalDevHours / totalCompletedPoints)
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableHeaderContext} ctx */
  const AverageHoursPerPointFooterTemplate = (ctx) => {
    const { table } = ctx
    const sprintsData = table.getRowModel().rows.map((row) => row.original)

    const totalCompletedPoints = sumColumnValuesWithoutTimeout(
      sprintsData,
      "totalCompletedPoints"
    )
    const totalDevHours = sumColumnValuesWithoutTimeout(
      sprintsData,
      "totalDevHours"
    )

    if (
      isNaN(totalCompletedPoints) ||
      isNaN(totalDevHours) ||
      totalCompletedPoints === 0 ||
      totalDevHours === 0
    )
      return "-"

    return numberFormatter(totalDevHours / totalCompletedPoints)
  }

  /** @param {import("./ClientSprints.controller.types").ClientSprintsTableCellContext<number | string>} ctx */
  const FormattedNumberCellTemplate = (ctx) => {
    const value = ctx.getValue()

    return numberFormatter(value)
  }

  return {
    SprintCellTemplate,
    PeriodCellTemplate,
    TotalCompletedPointsCellTemplate,
    TotalCompletedPointsFooterTemplate,
    TotalDevHoursFooterTemplate,
    AverageHoursPerPointCellTemplate,
    AverageHoursPerPointFooterTemplate,
    FormattedNumberCellTemplate,
  }
}
